import { useState } from "react";
import { Building2, AlertTriangle, Clock, CalendarClock, TrendingUp, PackageX, Activity, ArrowRight, Smartphone, X, Search, ChevronRight, ArrowLeft } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const ALL_BRANCHES = [
  { id: "kingswood",   name: "Kingswood Chemist",       color: "#7c3aed" },
  { id: "clifton",     name: "Clifton Care Pharmacy",    color: "#0ea5e9" },
  { id: "staple-hill", name: "Staple Hill Chemist",      color: "#10b981" },
  { id: "redland",     name: "Redland Pharmacy",         color: "#f59e0b" },
  { id: "ashton-gate", name: "Ashton Gate Pharmacy",     color: "#ef4444" },
  { id: "bedminster",  name: "Bedminster Pharmacy",      color: "#8b5cf6" },
  { id: "fishponds",   name: "Fishponds Pharmacy",       color: "#06b6d4" },
  { id: "henleaze",    name: "Henleaze Chemist",         color: "#ec4899" },
];

type ItemCategory = "Normal" | "CD" | "ERD" | "CD-ERD";

interface ExpiringItem {
  id: string;
  drug: string;
  pack: string;
  patient: string;
  rxDate: string;
  expiryDate: string;
  daysLeft: number;
  dtPrice: number;
  nhsReimbursement: number;
  costPrice: number;
  stage: "Received" | "Labelled" | "Dispensed" | "Checked" | "Awaiting Collection" | "Owing";
  epsBarcode: string;
  prescriber: string;
  treatmentType: "Acute" | "Repeat";
  branch: string;
  quantity: string;
  category: ItemCategory;
  issueNumber?: number;
  totalIssues?: number;
}

// Dispensing window per category (days from Rx date within which the item must be dispensed)
const dispensingWindowDays = (item: ExpiringItem): number => {
  const isFirstIssue = !item.issueNumber || item.issueNumber === 1;
  switch (item.category) {
    case "CD":      return 28;
    case "CD-ERD":  return isFirstIssue ? 28  : 365;
    case "ERD":     return isFirstIssue ? 183 : 365;
    case "Normal":
    default:        return 183;
  }
};

const CATEGORY_LABELS: Record<ItemCategory, string> = {
  "Normal":  "Normal",
  "CD":      "Controlled Drug",
  "ERD":     "ERD",
  "CD-ERD":  "CD ERD",
};

const CATEGORY_COLORS: Record<ItemCategory, { bg: string; text: string; bgLight: string; textLight: string }> = {
  "Normal":  { bg: "bg-sky-500/15",     text: "text-sky-400",     bgLight: "bg-sky-100",     textLight: "text-sky-700" },
  "CD":      { bg: "bg-rose-500/15",    text: "text-rose-400",    bgLight: "bg-rose-100",    textLight: "text-rose-700" },
  "ERD":     { bg: "bg-emerald-500/15", text: "text-emerald-400", bgLight: "bg-emerald-100", textLight: "text-emerald-700" },
  "CD-ERD":  { bg: "bg-fuchsia-500/15", text: "text-fuchsia-400", bgLight: "bg-fuchsia-100", textLight: "text-fuchsia-700" },
};

const expiringItemsData: Record<string, ExpiringItem[]> = {
  kingswood: [
    { id: "EXP-001", drug: "Amoxicillin 500mg Capsules", pack: "21 caps", patient: "John Smith", rxDate: "2026-03-15", expiryDate: "2026-04-02", daysLeft: 3, dtPrice: 1.07, nhsReimbursement: 1.07, costPrice: 0.52, stage: "Labelled", epsBarcode: "0E830A-A83008-42E5AH", prescriber: "Dr. R. Patel", treatmentType: "Acute", branch: "kingswood", quantity: "21", category: "Normal" },
    { id: "EXP-002", drug: "Omeprazole 20mg Capsules", pack: "28 caps", patient: "Sarah Johnson", rxDate: "2026-03-10", expiryDate: "2026-04-01", daysLeft: 2, dtPrice: 2.19, nhsReimbursement: 2.19, costPrice: 0.85, stage: "Dispensed", epsBarcode: "0E830A-A83008-51F2BK", prescriber: "Dr. L. Hughes", treatmentType: "Repeat", branch: "kingswood", quantity: "28", category: "ERD", issueNumber: 1, totalIssues: 4 },
    { id: "EXP-003", drug: "Metformin 500mg Tablets", pack: "56 tabs", patient: "David Williams", rxDate: "2026-03-12", expiryDate: "2026-04-05", daysLeft: 6, dtPrice: 1.78, nhsReimbursement: 1.78, costPrice: 0.91, stage: "Received", epsBarcode: "0E830A-A83008-63G7CM", prescriber: "Dr. R. Patel", treatmentType: "Repeat", branch: "kingswood", quantity: "56", category: "ERD", issueNumber: 2, totalIssues: 4 },
    { id: "EXP-004", drug: "Atorvastatin 40mg Tablets", pack: "28 tabs", patient: "Margaret Brown", rxDate: "2026-03-08", expiryDate: "2026-03-31", daysLeft: 1, dtPrice: 1.14, nhsReimbursement: 1.14, costPrice: 0.42, stage: "Awaiting Collection", epsBarcode: "0E830A-A83008-74H8DN", prescriber: "Dr. S. Ahmed", treatmentType: "Repeat", branch: "kingswood", quantity: "28", category: "Normal" },
    { id: "EXP-005", drug: "Salbutamol 100mcg Inhaler", pack: "1 inhaler", patient: "James Taylor", rxDate: "2026-03-18", expiryDate: "2026-04-08", daysLeft: 9, dtPrice: 1.50, nhsReimbursement: 1.50, costPrice: 0.89, stage: "Checked", epsBarcode: "0E830A-A83008-85I9EP", prescriber: "Dr. L. Hughes", treatmentType: "Acute", branch: "kingswood", quantity: "1", category: "Normal" },
    { id: "EXP-006", drug: "Amlodipine 5mg Tablets", pack: "28 tabs", patient: "Patricia Davis", rxDate: "2026-03-05", expiryDate: "2026-03-30", daysLeft: 0, dtPrice: 0.87, nhsReimbursement: 0.87, costPrice: 0.31, stage: "Labelled", epsBarcode: "0E830A-A83008-96J0FQ", prescriber: "Dr. R. Patel", treatmentType: "Repeat", branch: "kingswood", quantity: "28", category: "Normal" },
    { id: "EXP-007", drug: "Morphine Sulfate 10mg/5ml Oral Solution", pack: "100ml", patient: "Robert Wilson", rxDate: "2026-03-20", expiryDate: "2026-04-12", daysLeft: 13, dtPrice: 8.80, nhsReimbursement: 8.80, costPrice: 4.10, stage: "Received", epsBarcode: "0E830A-A83008-07K1GR", prescriber: "Dr. S. Ahmed", treatmentType: "Acute", branch: "kingswood", quantity: "100", category: "CD" },
    { id: "EXP-008", drug: "Doxycycline 100mg Capsules", pack: "8 caps", patient: "Linda Martinez", rxDate: "2026-03-22", expiryDate: "2026-04-03", daysLeft: 4, dtPrice: 3.45, nhsReimbursement: 3.45, costPrice: 1.88, stage: "Dispensed", epsBarcode: "0E830A-A83008-18L2HS", prescriber: "Dr. L. Hughes", treatmentType: "Acute", branch: "kingswood", quantity: "8", category: "Normal" },
    { id: "EXP-009", drug: "Methylphenidate 10mg Tablets", pack: "30 tabs", patient: "Thomas Anderson", rxDate: "2026-03-01", expiryDate: "2026-03-29", daysLeft: -2, dtPrice: 12.93, nhsReimbursement: 12.93, costPrice: 6.20, stage: "Owing", epsBarcode: "0E830A-A83008-29M3IT", prescriber: "Dr. R. Patel", treatmentType: "Repeat", branch: "kingswood", quantity: "30", category: "CD-ERD", issueNumber: 1, totalIssues: 3 },
    { id: "EXP-010", drug: "Flucloxacillin 500mg Capsules", pack: "28 caps", patient: "Jennifer Lee", rxDate: "2026-03-25", expiryDate: "2026-04-15", daysLeft: 16, dtPrice: 5.12, nhsReimbursement: 5.12, costPrice: 2.95, stage: "Received", epsBarcode: "0E830A-A83008-30N4JU", prescriber: "Dr. S. Ahmed", treatmentType: "Acute", branch: "kingswood", quantity: "28", category: "Normal" },
    { id: "EXP-011", drug: "Ramipril 5mg Capsules", pack: "28 caps", patient: "Michael Garcia", rxDate: "2026-03-14", expiryDate: "2026-04-04", daysLeft: 5, dtPrice: 1.21, nhsReimbursement: 1.21, costPrice: 0.48, stage: "Checked", epsBarcode: "0E830A-A83008-41O5KV", prescriber: "Dr. L. Hughes", treatmentType: "Repeat", branch: "kingswood", quantity: "28", category: "ERD", issueNumber: 3, totalIssues: 6 },
    { id: "EXP-012", drug: "Diazepam 5mg Tablets", pack: "28 tabs", patient: "Elizabeth Clark", rxDate: "2026-03-01", expiryDate: "2026-03-28", daysLeft: -3, dtPrice: 4.32, nhsReimbursement: 4.32, costPrice: 1.75, stage: "Labelled", epsBarcode: "0E830A-A83008-52P6LW", prescriber: "Dr. R. Patel", treatmentType: "Acute", branch: "kingswood", quantity: "28", category: "CD" },
    { id: "EXP-013", drug: "Tramadol 50mg Capsules", pack: "30 caps", patient: "Daniel Roberts", rxDate: "2026-03-04", expiryDate: "2026-03-26", daysLeft: -5, dtPrice: 3.95, nhsReimbursement: 3.95, costPrice: 1.55, stage: "Awaiting Collection", epsBarcode: "0E830A-A83008-63Q7MX", prescriber: "Dr. S. Ahmed", treatmentType: "Acute", branch: "kingswood", quantity: "30", category: "CD" },
  ],
  clifton: [
    { id: "EXP-C01", drug: "Lisinopril 10mg Tablets", pack: "28 tabs", patient: "Emma Watson", rxDate: "2026-03-16", expiryDate: "2026-04-01", daysLeft: 2, dtPrice: 0.95, nhsReimbursement: 0.95, costPrice: 0.38, stage: "Labelled", epsBarcode: "0F940B-B94019-11A1AA", prescriber: "Dr. K. Chen", treatmentType: "Repeat", branch: "clifton", quantity: "28", category: "Normal" },
    { id: "EXP-C02", drug: "Sertraline 50mg Tablets", pack: "28 tabs", patient: "Oliver Brown", rxDate: "2026-03-11", expiryDate: "2026-03-31", daysLeft: 1, dtPrice: 1.36, nhsReimbursement: 1.36, costPrice: 0.55, stage: "Awaiting Collection", epsBarcode: "0F940B-B94019-22B2BB", prescriber: "Dr. K. Chen", treatmentType: "Repeat", branch: "clifton", quantity: "28", category: "ERD", issueNumber: 1, totalIssues: 4 },
    { id: "EXP-C03", drug: "Amitriptyline 25mg Tablets", pack: "28 tabs", patient: "Sophia Miller", rxDate: "2026-03-20", expiryDate: "2026-04-10", daysLeft: 11, dtPrice: 0.78, nhsReimbursement: 0.78, costPrice: 0.25, stage: "Received", epsBarcode: "0F940B-B94019-33C3CC", prescriber: "Dr. M. Singh", treatmentType: "Repeat", branch: "clifton", quantity: "28", category: "Normal" },
    { id: "EXP-C04", drug: "Pethidine 50mg Tablets", pack: "20 tabs", patient: "Jack Thompson", rxDate: "2026-03-04", expiryDate: "2026-03-27", daysLeft: -4, dtPrice: 6.28, nhsReimbursement: 6.28, costPrice: 3.10, stage: "Dispensed", epsBarcode: "0F940B-B94019-44D4DD", prescriber: "Dr. M. Singh", treatmentType: "Acute", branch: "clifton", quantity: "20", category: "CD" },
  ],
  "staple-hill": [
    { id: "EXP-S01", drug: "Levothyroxine 50mcg Tablets", pack: "28 tabs", patient: "Grace Evans", rxDate: "2026-03-09", expiryDate: "2026-03-30", daysLeft: 0, dtPrice: 1.92, nhsReimbursement: 1.92, costPrice: 0.72, stage: "Awaiting Collection", epsBarcode: "0G050C-C05020-11E1EE", prescriber: "Dr. P. Jones", treatmentType: "Repeat", branch: "staple-hill", quantity: "28", category: "ERD", issueNumber: 2, totalIssues: 4 },
    { id: "EXP-S02", drug: "Naproxen 500mg Tablets", pack: "28 tabs", patient: "Harry White", rxDate: "2026-03-17", expiryDate: "2026-04-07", daysLeft: 8, dtPrice: 2.45, nhsReimbursement: 2.45, costPrice: 1.15, stage: "Checked", epsBarcode: "0G050C-C05020-22F2FF", prescriber: "Dr. P. Jones", treatmentType: "Acute", branch: "staple-hill", quantity: "28", category: "Normal" },
    { id: "EXP-S03", drug: "Oxycodone 5mg Capsules", pack: "28 caps", patient: "Olivia Harris", rxDate: "2026-03-02", expiryDate: "2026-03-28", daysLeft: -1, dtPrice: 9.05, nhsReimbursement: 9.05, costPrice: 4.42, stage: "Labelled", epsBarcode: "0G050C-C05020-33G3GG", prescriber: "Dr. P. Jones", treatmentType: "Acute", branch: "staple-hill", quantity: "28", category: "CD" },
  ],
};

const STAGE_COLORS: Record<string, { bg: string; text: string; bgLight: string; textLight: string }> = {
  Received:              { bg: "bg-slate-500/15",  text: "text-slate-400",  bgLight: "bg-slate-100",  textLight: "text-slate-700" },
  Labelled:              { bg: "bg-blue-500/15",   text: "text-blue-400",   bgLight: "bg-blue-100",   textLight: "text-blue-700"  },
  Dispensed:             { bg: "bg-indigo-500/15", text: "text-indigo-400", bgLight: "bg-indigo-100", textLight: "text-indigo-700" },
  Checked:               { bg: "bg-violet-500/15", text: "text-violet-400", bgLight: "bg-violet-100", textLight: "text-violet-700" },
  "Awaiting Collection": { bg: "bg-amber-500/15",  text: "text-amber-400",  bgLight: "bg-amber-100",  textLight: "text-amber-700" },
  Owing:                 { bg: "bg-red-500/15",    text: "text-red-400",    bgLight: "bg-red-100",    textLight: "text-red-700"   },
};

export default function ExpiringItemsTab({ isDarkMode, selectedBranches, onBranchChange }: { isDarkMode: boolean; selectedBranches: string[]; onBranchChange: (id: string) => void }) {
  const [showOverview, setShowOverview] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"all" | "expiring" | "expired">("all");
  const [categoryFilter, setCategoryFilter] = useState<"all" | ItemCategory>("all");
  const [urgencyFilter, setUrgencyFilter] = useState<"all" | "critical" | "soon" | "upcoming">("all");
  const [stageFilter, setStageFilter] = useState<string>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");

  const activeBranch = selectedBranches[0] || "kingswood";
  const activeBranchData = ALL_BRANCHES.find((b) => b.id === activeBranch);
  const allItems = expiringItemsData[activeBranch] || [];

  const isExpired = (i: ExpiringItem) => i.daysLeft < 0;

  // Filter items
  const filteredItems = allItems.filter((item) => {
    if (statusFilter === "expired" && !isExpired(item)) return false;
    if (statusFilter === "expiring" && isExpired(item)) return false;
    if (categoryFilter !== "all" && item.category !== categoryFilter) return false;
    if (urgencyFilter === "critical" && (isExpired(item) || item.daysLeft > 2)) return false;
    if (urgencyFilter === "soon" && (item.daysLeft < 3 || item.daysLeft > 7)) return false;
    if (urgencyFilter === "upcoming" && item.daysLeft <= 7) return false;
    if (stageFilter !== "all" && item.stage !== stageFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return item.drug.toLowerCase().includes(s) || item.patient.toLowerCase().includes(s) || item.epsBarcode.toLowerCase().includes(s);
    }
    return true;
  });

  // Stats
  const expiredCount  = allItems.filter(isExpired).length;
  const expiringCount = allItems.filter((i) => !isExpired(i)).length;
  const criticalCount = allItems.filter((i) => !isExpired(i) && i.daysLeft <= 2).length;
  const soonCount = allItems.filter((i) => i.daysLeft >= 3 && i.daysLeft <= 7).length;
  const upcomingCount = allItems.filter((i) => i.daysLeft > 7).length;

  const categoryCounts: Record<ItemCategory, number> = {
    "Normal":  allItems.filter((i) => i.category === "Normal").length,
    "CD":      allItems.filter((i) => i.category === "CD").length,
    "ERD":     allItems.filter((i) => i.category === "ERD").length,
    "CD-ERD":  allItems.filter((i) => i.category === "CD-ERD").length,
  };

  const totalDtValue = allItems.reduce((sum, i) => sum + i.dtPrice, 0);
  const totalNhsValue = allItems.reduce((sum, i) => sum + i.nhsReimbursement, 0);
  const totalCostValue = allItems.reduce((sum, i) => sum + i.costPrice, 0);
  const totalMargin = totalNhsValue - totalCostValue;
  const atRiskValue = allItems.filter((i) => !isExpired(i) && i.daysLeft <= 2).reduce((sum, i) => sum + i.dtPrice, 0);
  const expiredValue = allItems.filter(isExpired).reduce((sum, i) => sum + i.dtPrice, 0);
  const awaitingCollection = allItems.filter((i) => i.stage === "Awaiting Collection").length;
  const notYetDispensed = allItems.filter((i) => i.stage === "Received" || i.stage === "Labelled").length;

  const toggleSelect = (id: string) => setSelectedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => {
    if (selectedIds.size === filteredItems.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredItems.map((i) => i.id)));
  };

  const getDaysLeftColor = (days: number) => {
    if (days < 0)  return isDarkMode ? "text-red-300 bg-red-500/25"  : "text-red-800 bg-red-200";
    if (days === 0) return isDarkMode ? "text-red-400 bg-red-500/15" : "text-red-700 bg-red-100";
    if (days <= 2) return isDarkMode ? "text-orange-400 bg-orange-500/15" : "text-orange-700 bg-orange-100";
    if (days <= 7) return isDarkMode ? "text-amber-400 bg-amber-500/15" : "text-amber-700 bg-amber-100";
    return isDarkMode ? "text-green-400 bg-green-500/15" : "text-green-700 bg-green-100";
  };

  const getDaysLabel = (days: number) => {
    if (days < 0)  return `EXPIRED ${Math.abs(days)}d`;
    if (days === 0) return "TODAY";
    if (days === 1) return "1 day";
    return `${days} days`;
  };

  if (showOverview) {
    const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
    const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
    const estateExpired = ALL_BRANCHES.reduce((s, b) => s + (expiringItemsData[b.id] ?? []).filter(i => i.daysLeft < 0).length, 0);
    const estateExpiring = ALL_BRANCHES.reduce((s, b) => s + (expiringItemsData[b.id] ?? []).filter(i => i.daysLeft >= 0).length, 0);
    return (
      <div className="p-6">
        <div className="mb-6">
          <h2 className={`text-lg font-semibold ${textPrimary}`}>Expiring Items — All Branches</h2>
          <p className={`text-sm mt-0.5 ${textSecondary}`}>{estateExpiring} expiring · {estateExpired} expired across estate</p>
        </div>
        <div className={`rounded-xl border overflow-hidden ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className={isDarkMode ? "bg-gray-800/60" : "bg-gray-50"}>
                {["Branch", "Total", "Expired", "Expiring", "Critical (≤2d)", "Action Needed", "At-Risk Value", ""].map(h => (
                  <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? "divide-gray-800" : "divide-gray-100"}`}>
              {ALL_BRANCHES.map(b => {
                const items = expiringItemsData[b.id] ?? [];
                const expired = items.filter(i => i.daysLeft < 0).length;
                const expiring = items.filter(i => i.daysLeft >= 0).length;
                const critical = items.filter(i => i.daysLeft >= 0 && i.daysLeft <= 2).length;
                const actionNeeded = items.filter(i => i.stage === "Awaiting Collection" || i.stage === "Received" || i.stage === "Labelled").length;
                const atRisk = items.filter(i => i.daysLeft >= 0 && i.daysLeft <= 2).reduce((s, i) => s + i.dtPrice, 0);
                const hasData = items.length > 0;
                return (
                  <tr
                    key={b.id}
                    onClick={() => { onBranchChange(b.id); setShowOverview(false); }}
                    className={`cursor-pointer transition-colors ${isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"}`}
                  >
                    <td className={`px-4 py-3.5 font-medium ${textPrimary}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: b.color }} />
                        {b.name}
                      </div>
                    </td>
                    <td className={`px-4 py-3.5 ${hasData ? textPrimary : textSecondary}`}>{items.length || "—"}</td>
                    <td className={`px-4 py-3.5 font-semibold ${expired > 0 ? "text-red-500" : textSecondary}`}>{expired || "—"}</td>
                    <td className={`px-4 py-3.5 ${expiring > 0 ? (isDarkMode ? "text-amber-400" : "text-amber-600") : textSecondary}`}>{expiring || "—"}</td>
                    <td className={`px-4 py-3.5 font-semibold ${critical > 0 ? "text-red-500" : textSecondary}`}>{critical || "—"}</td>
                    <td className={`px-4 py-3.5 font-semibold ${actionNeeded > 0 ? (isDarkMode ? "text-amber-400" : "text-amber-600") : textSecondary}`}>{actionNeeded || "—"}</td>
                    <td className={`px-4 py-3.5 font-mono ${atRisk > 0 ? "text-red-500 font-semibold" : textSecondary}`}>{atRisk > 0 ? `£${atRisk.toFixed(2)}` : "—"}</td>
                    <td className="px-4 py-3.5 text-right">
                      <ChevronRight className={`w-4 h-4 inline-block ${isDarkMode ? "text-gray-600" : "text-gray-400"}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => setShowOverview(true)} className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg cursor-pointer transition-colors ${isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            <ArrowLeft className="w-3.5 h-3.5" /> All Branches
          </button>
        </div>
        <h2 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>Expiring &amp; Expired Items</h2>
        <p className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
          {expiringCount} expiring · {expiredCount} expired prescription items for {activeBranchData?.name ?? activeBranch}
        </p>
        <div className="flex items-center gap-2 mt-2.5">
          <Building2 className={`w-4 h-4 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
          <select
            value={activeBranch}
            onChange={(e) => onBranchChange(e.target.value)}
            className={`text-sm font-medium rounded-lg border px-3 py-1.5 cursor-pointer outline-none transition-colors ${isDarkMode ? "bg-gray-800 border-gray-700 text-gray-200 hover:border-gray-600" : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"}`}
          >
            {ALL_BRANCHES.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>

      {/* Headline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        <button
          onClick={() => setStatusFilter(statusFilter === "expired" ? "all" : "expired")}
          className={`text-left rounded-xl p-4 border transition-colors ${
            statusFilter === "expired"
              ? isDarkMode ? "bg-red-500/20 border-red-500/40" : "bg-red-100 border-red-300"
              : isDarkMode ? "bg-red-500/10 border-red-500/20 hover:bg-red-500/15" : "bg-red-50 border-red-200 hover:bg-red-100"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <PackageX className={`w-4 h-4 ${isDarkMode ? "text-red-400" : "text-red-600"}`} />
            <span className={`text-xs font-medium ${isDarkMode ? "text-red-400" : "text-red-600"}`}>Expired</span>
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-red-300" : "text-red-700"}`}>{expiredCount}</p>
          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>£{expiredValue.toFixed(2)} loss</p>
        </button>
        <button
          onClick={() => setStatusFilter(statusFilter === "expiring" ? "all" : "expiring")}
          className={`text-left rounded-xl p-4 border transition-colors ${
            statusFilter === "expiring"
              ? isDarkMode ? "bg-amber-500/20 border-amber-500/40" : "bg-amber-100 border-amber-300"
              : isDarkMode ? "bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/15" : "bg-amber-50 border-amber-200 hover:bg-amber-100"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Clock className={`w-4 h-4 ${isDarkMode ? "text-amber-400" : "text-amber-600"}`} />
            <span className={`text-xs font-medium ${isDarkMode ? "text-amber-400" : "text-amber-600"}`}>Expiring</span>
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-amber-300" : "text-amber-700"}`}>{expiringCount}</p>
          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{criticalCount} critical · {soonCount} soon · {upcomingCount} upcoming</p>
        </button>
        <div className={`rounded-xl p-4 border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className={`w-4 h-4 ${isDarkMode ? "text-red-400" : "text-red-500"}`} />
            <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>At-Risk Value</span>
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-red-300" : "text-red-600"}`}>£{atRiskValue.toFixed(2)}</p>
          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Items expiring ≤2 days</p>
        </div>
        <div className={`rounded-xl p-4 border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className={`w-4 h-4 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />
            <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Total DT Value</span>
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>£{totalDtValue.toFixed(2)}</p>
          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Margin: £{totalMargin.toFixed(2)}</p>
        </div>
        <div className={`rounded-xl p-4 border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
          <div className="flex items-center gap-2 mb-1">
            <Activity className={`w-4 h-4 ${isDarkMode ? "text-violet-400" : "text-violet-600"}`} />
            <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Action Needed</span>
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{awaitingCollection + notYetDispensed}</p>
          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{awaitingCollection} awaiting · {notYetDispensed} not dispensed</p>
        </div>
        <div className={`rounded-xl p-4 border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
          <div className="flex items-center gap-2 mb-1">
            <CalendarClock className={`w-4 h-4 ${isDarkMode ? "text-indigo-400" : "text-indigo-600"}`} />
            <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>By Type</span>
          </div>
          <p className={`text-xs leading-5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
            <span className="font-semibold">{categoryCounts.Normal}</span> Normal · <span className="font-semibold">{categoryCounts.CD}</span> CD<br/>
            <span className="font-semibold">{categoryCounts.ERD}</span> ERD · <span className="font-semibold">{categoryCounts["CD-ERD"]}</span> CD ERD
          </p>
        </div>
      </div>

      {/* Category Sub-division */}
      <div className={`rounded-xl p-3 border ${isDarkMode ? "bg-gray-800/40 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-semibold uppercase tracking-wide mr-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Category</span>
          {(["all", "Normal", "CD", "ERD", "CD-ERD"] as const).map((c) => {
            const active = categoryFilter === c;
            const count = c === "all" ? allItems.length : categoryCounts[c];
            const labels: Record<string, string> = { all: "All", Normal: "Normal (6 mo)", CD: "Controlled Drug (28 d)", ERD: "ERD", "CD-ERD": "CD ERD" };
            return (
              <button
                key={c}
                onClick={() => setCategoryFilter(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  active
                    ? isDarkMode ? "bg-white text-black" : "bg-gray-900 text-white"
                    : isDarkMode ? "bg-gray-700/60 text-gray-300 hover:bg-gray-700" : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
                }`}
              >
                {labels[c]} <span className={active ? "opacity-80" : "opacity-60"}>{count}</span>
              </button>
            );
          })}
          <div className="flex-1" />
          <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
            ERD: 1st issue dispensed within 6 mo (28 d if CD) · remaining issues within 12 mo
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          {(["all", "expiring", "expired"] as const).map((f) => {
            const labels: Record<string, string> = { all: "All Status", expiring: "Expiring", expired: "Expired" };
            const counts: Record<string, number> = { all: allItems.length, expiring: expiringCount, expired: expiredCount };
            const active = statusFilter === f;
            return (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  active
                    ? f === "expired"
                      ? "bg-red-600 text-white"
                      : f === "expiring"
                      ? "bg-amber-600 text-white"
                      : isDarkMode ? "bg-white text-black" : "bg-gray-900 text-white"
                    : isDarkMode ? "bg-gray-800 text-gray-400 hover:bg-gray-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {labels[f]} <span className={`ml-1 ${active ? "opacity-80" : "opacity-60"}`}>{counts[f]}</span>
              </button>
            );
          })}
        </div>
        <div className={`h-5 w-px ${isDarkMode ? "bg-gray-700" : "bg-gray-300"}`} />
        <div className="flex items-center gap-1">
          {(["all", "critical", "soon", "upcoming"] as const).map((f) => {
            const labels: Record<string, string> = { all: "All", critical: "Critical", soon: "Soon", upcoming: "Upcoming" };
            const counts: Record<string, number> = { all: allItems.length, critical: criticalCount, soon: soonCount, upcoming: upcomingCount };
            const active = urgencyFilter === f;
            return (
              <button
                key={f}
                onClick={() => setUrgencyFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  active
                    ? isDarkMode ? "bg-white text-black" : "bg-gray-900 text-white"
                    : isDarkMode ? "bg-gray-800 text-gray-400 hover:bg-gray-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {labels[f]} <span className={`ml-1 ${active ? "opacity-80" : "opacity-60"}`}>{counts[f]}</span>
              </button>
            );
          })}
        </div>
        <div className={`h-5 w-px ${isDarkMode ? "bg-gray-700" : "bg-gray-300"}`} />
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className={`text-xs rounded-lg px-2.5 py-1.5 border ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
        >
          <option value="all">All Stages</option>
          <option value="Received">Received</option>
          <option value="Labelled">Labelled</option>
          <option value="Dispensed">Dispensed</option>
          <option value="Checked">Checked</option>
          <option value="Awaiting Collection">Awaiting Collection</option>
          <option value="Owing">Owing</option>
        </select>
        <div className="flex-1" />
        <div className="relative">
          <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search drug, patient, barcode..."
            className={`pl-8 pr-3 py-1.5 text-xs rounded-lg border w-56 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"}`}
          />
        </div>
      </div>

      {/* Selected actions bar */}
      {selectedIds.size > 0 && (
        <div className={`flex items-center gap-3 p-3 rounded-xl border ${isDarkMode ? "bg-indigo-500/10 border-indigo-500/30" : "bg-indigo-50 border-indigo-200"}`}>
          <span className={`text-sm font-medium ${isDarkMode ? "text-indigo-300" : "text-indigo-700"}`}>{selectedIds.size} item{selectedIds.size > 1 ? "s" : ""} selected</span>
          <div className="flex-1" />
          <button className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? "bg-green-600 text-white hover:bg-green-500" : "bg-green-600 text-white hover:bg-green-700"}`}>
            <ArrowRight className="w-3.5 h-3.5" /> Push Forward
          </button>
          <button className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? "bg-amber-600 text-white hover:bg-amber-500" : "bg-amber-600 text-white hover:bg-amber-700"}`}>
            <Smartphone className="w-3.5 h-3.5" /> Contact Patient
          </button>
          <button
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? "bg-gray-700 text-gray-200 hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
            onClick={() => setSelectedIds(new Set())}
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className={`rounded-xl border overflow-hidden ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDarkMode ? "bg-gray-800/60" : "bg-gray-50"}>
                <th className="px-3 py-2.5 text-left w-10">
                  <Checkbox checked={selectedIds.size === filteredItems.length && filteredItems.length > 0} onCheckedChange={toggleAll} />
                </th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Days Left</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Type</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Drug</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Patient</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Stage</th>
                <th className={`px-3 py-2.5 text-right text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>DT Price</th>
                <th className={`px-3 py-2.5 text-right text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>NHS Reimb.</th>
                <th className={`px-3 py-2.5 text-right text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Cost Price</th>
                <th className={`px-3 py-2.5 text-right text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Margin</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Rx Date</th>
                <th className={`px-3 py-2.5 text-left text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Expiry</th>
                <th className={`px-3 py-2.5 text-center text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Action</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? "divide-gray-800" : "divide-gray-100"}`}>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={13} className={`px-3 py-12 text-center text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    No items match the current filters
                  </td>
                </tr>
              ) : (
                filteredItems
                  .sort((a, b) => a.daysLeft - b.daysLeft)
                  .map((item) => {
                    const margin = item.nhsReimbursement - item.costPrice;
                    const sc = STAGE_COLORS[item.stage] || STAGE_COLORS.Received;
                    const cc = CATEGORY_COLORS[item.category];
                    const window = dispensingWindowDays(item);
                    return (
                      <tr
                        key={item.id}
                        className={`transition-colors ${
                          selectedIds.has(item.id)
                            ? isDarkMode ? "bg-indigo-500/10" : "bg-indigo-50"
                            : isDarkMode ? "hover:bg-gray-800/40" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-3 py-2.5">
                          <Checkbox checked={selectedIds.has(item.id)} onCheckedChange={() => toggleSelect(item.id)} />
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${getDaysLeftColor(item.daysLeft)}`}>
                            {getDaysLabel(item.daysLeft)}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${isDarkMode ? cc.bg + " " + cc.text : cc.bgLight + " " + cc.textLight}`}>
                            {CATEGORY_LABELS[item.category]}
                          </span>
                          {item.issueNumber && item.totalIssues && (
                            <p className={`text-[10px] mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                              Issue {item.issueNumber}/{item.totalIssues} · {window}d window
                            </p>
                          )}
                          {!item.issueNumber && (
                            <p className={`text-[10px] mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                              {window}d dispense window
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <div>
                            <p className={`text-xs font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>{item.drug}</p>
                            <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{item.pack} · Qty: {item.quantity}</p>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div>
                            <p className={`text-xs font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>{item.patient}</p>
                            <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{item.prescriber}</p>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? sc.bg + " " + sc.text : sc.bgLight + " " + sc.textLight}`}>
                            {item.stage}
                          </span>
                        </td>
                        <td className={`px-3 py-2.5 text-right text-xs font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
                          £{item.dtPrice.toFixed(2)}
                        </td>
                        <td className={`px-3 py-2.5 text-right text-xs font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
                          £{item.nhsReimbursement.toFixed(2)}
                        </td>
                        <td className={`px-3 py-2.5 text-right text-xs font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
                          £{item.costPrice.toFixed(2)}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <span className={`text-xs font-bold ${margin >= 0 ? isDarkMode ? "text-green-400" : "text-green-600" : isDarkMode ? "text-red-400" : "text-red-600"}`}>
                            £{margin.toFixed(2)}
                          </span>
                        </td>
                        <td className={`px-3 py-2.5 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                          {new Date(item.rxDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                        </td>
                        <td className={`px-3 py-2.5 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                          {new Date(item.expiryDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <button className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                            isDarkMode ? "bg-indigo-600 text-white hover:bg-indigo-500" : "bg-indigo-600 text-white hover:bg-indigo-700"
                          }`}>
                            <ArrowRight className="w-3 h-3" /> Push
                          </button>
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer summary */}
      <div className={`flex items-center justify-between text-xs py-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
        <span>Showing {filteredItems.length} of {allItems.length} items ({expiringCount} expiring · {expiredCount} expired)</span>
        <span>Total DT: £{totalDtValue.toFixed(2)} · NHS Reimb: £{totalNhsValue.toFixed(2)} · Cost: £{totalCostValue.toFixed(2)} · Margin: £{totalMargin.toFixed(2)}</span>
      </div>
    </div>
  );
}
